import logger from 'utils/logger';

import {
  AppPersonalizedOffer,
  AppPersonalizedOfferGroup,
  EnginePersonalizedOffer,
  EnginePersonalizedOfferGroup,
  SanityId,
  SanityOfferGroupHeader,
  SanitySystemwideOffer,
  buildAppPersonalizedOfferFromSystemwide,
  buildAppPersonalizedOfferGroup,
} from '../../types';

type LoggerLike = Pick<typeof logger, 'warn'>;

// Keyed by Systemwide Offer loyalty engine id (matches `engineOffer.configId` for POG child offers)
export type SystemwideOffersMap = Record<string, SanitySystemwideOffer>;
export type OfferGroupsMap = Record<SanityId, SanityOfferGroupHeader>;

export function getPersonalizedOfferFromGroupOffer(
  engineOffer: EnginePersonalizedOffer,
  systemwideOffersMap: SystemwideOffersMap,
  log: LoggerLike = logger
): AppPersonalizedOffer | null {
  const configId = engineOffer?.configId;
  if (!configId) {
    log.warn({
      message: 'Skipping POG offer due to missing configId',
      offerId: engineOffer?.id,
    });
    return null;
  }

  // NOTE: For offers inside POGs, engineOffer.configId maps to the Systemwide Offer loyalty engine id.
  const systemwideOffer = (systemwideOffersMap as any)[configId];
  if (!systemwideOffer) {
    log.warn({
      message: 'Skipping POG offer due to missing systemwide offer CMS data',
      offerId: engineOffer?.id,
      configId,
    });
    return null;
  }

  return buildAppPersonalizedOfferFromSystemwide(systemwideOffer, engineOffer);
}

export function processPersonalizedOfferGroup(
  engineGroup: EnginePersonalizedOfferGroup,
  systemwideOffersMap: SystemwideOffersMap,
  offerGroupsMap: OfferGroupsMap,
  log: LoggerLike = logger
): AppPersonalizedOfferGroup | null {
  const appOffers = engineGroup.offers
    .map(engineOffer => getPersonalizedOfferFromGroupOffer(engineOffer, systemwideOffersMap, log))
    .filter(Boolean) as AppPersonalizedOffer[];

  if (!appOffers.length) {
    log.warn({
      message: 'Hiding POG due to no resolvable child offers',
      groupId: engineGroup.id,
    });
    return null;
  }

  const cmsHeader = offerGroupsMap[(engineGroup.cmsId as unknown) as SanityId];
  return buildAppPersonalizedOfferGroup(engineGroup, appOffers, cmsHeader);
}
